import {
  AppBar,
  Box,
  Button,
  Drawer,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import COLORS from '@/resources/style/Themes';
import { StringKeys } from '@/resources/strings/StringKeys';

type DeviceManagementRouteBarProps = {
  onRefresh: () => void;
  isLoading: boolean;
};

/**
 * Left sidebar chrome for the Device Management page. Matches the other
 * management pages' route bars; the Refresh control lives in the header.
 */
const DeviceManagementRouteBar = ({
  onRefresh,
  isLoading,
}: DeviceManagementRouteBarProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const refreshButton = (
    <Button
      variant="contained"
      color="secondary"
      onClick={onRefresh}
      disabled={isLoading}
      startIcon={<RefreshIcon />}
    >
      {t(StringKeys.REFRESH)}
    </Button>
  );

  const renderMobileView = () => (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: COLORS.WHITE,
        boxShadow: 0,
        top: 64,
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ px: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Typography sx={{ color: '#3A3B3C' }}>
          {t(StringKeys.DEVICE_MANAGEMENT)}
        </Typography>
        {refreshButton}
      </Toolbar>
    </AppBar>
  );

  const renderDesktopView = () => (
    <Drawer
      variant="permanent"
      sx={{
        width: 240,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: 240,
          boxSizing: 'border-box',
          top: 65,
          background: '#ffffff',
          height: 'calc(100vh - 65px)',
          overflowY: 'auto',
        },
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: '#3e5371',
          boxShadow: 3,
          color: COLORS.WHITE,
          padding: '10px',
          paddingTop: '20px',
          paddingBottom: '20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 1,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 300, textAlign: 'left' }}>
          {t(StringKeys.DEVICE_MANAGEMENT)}
        </Typography>
        <Typography variant="body2" sx={{ color: COLORS.WHITE }}>
          {t(StringKeys.DEVICE_MANAGEMENT_BLURB)}
        </Typography>
        {refreshButton}
      </Box>
    </Drawer>
  );

  return isMobile ? renderMobileView() : renderDesktopView();
};

export default DeviceManagementRouteBar;
